import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, UploadCloud, PenLine, Eye, EyeOff, FileText,
} from 'lucide-react';
import { sopApi, formatBytes } from './api';
import TagPicker from './components/TagPicker';
import FileDropzone from './components/FileDropzone';
import { MarkdownBody } from './viewers/UniversalViewer';

const MARKDOWN_HINT = `# Section heading

Write the procedure here.

1. First step
2. Second step

- Bullet point
- **Bold** and *italic* and \`inline code\`

> Callout or warning

| Step | Owner |
| --- | --- |
| Unload | Dock lead |`;

const SopEditor = () => {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!sopId;

  const [source, setSource] = useState('file'); // 'file' | 'native'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [status, setStatus] = useState('published');
  const [file, setFile] = useState(null);
  const [bodyMarkdown, setBodyMarkdown] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const [allTags, setAllTags] = useState([]);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    sopApi.listTags().then((r) => setAllTags(r.data || [])).catch(() => setAllTags([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    sopApi.get(sopId)
      .then((r) => {
        const d = r.data;
        setExisting(d);
        setTitle(d.title);
        setDescription(d.description || '');
        setTags(d.tags || []);
        setStatus(d.status);
        setSource(d.docKind === 'native' ? 'native' : 'file');
        setBodyMarkdown(d.bodyMarkdown || '');
      })
      .catch((err) => setError(err.message || 'Failed to load SOP'))
      .finally(() => setLoading(false));
  }, [isEdit, sopId]);

  // On edit, the file/body only changes if the author supplies something new —
  // otherwise saving just updates the metadata and leaves the version alone.
  const willAddVersion = useMemo(() => {
    if (!isEdit) return false;
    if (source === 'file') return !!file;
    return bodyMarkdown.trim() !== (existing?.bodyMarkdown || '').trim() && bodyMarkdown.trim().length > 0;
  }, [isEdit, source, file, bodyMarkdown, existing]);

  const canSave = title.trim() &&
    (isEdit || (source === 'file' ? !!file : !!bodyMarkdown.trim())) &&
    !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    setSaving(true);
    setError('');

    try {
      if (!isEdit) {
        const form = new FormData();
        form.append('title', title.trim());
        form.append('description', description.trim());
        form.append('status', status);
        form.append('docKind', source === 'native' ? 'native' : 'file');
        form.append('tags', JSON.stringify(tags.map((t) => (t.id ? { id: t.id } : { name: t.name }))));
        if (source === 'file' && file) form.append('file', file);
        if (source === 'native') form.append('bodyMarkdown', bodyMarkdown);

        const res = await sopApi.create(form);
        navigate(`/tiles/ksp_sops/view/${res.data.id}`);
        return;
      }

      await sopApi.update(sopId, {
        title: title.trim(),
        description: description.trim(),
        status,
        tags: tags.map((t) => (t.id ? { id: t.id } : { name: t.name })),
      });

      if (willAddVersion) {
        if (source === 'file' && file) {
          const form = new FormData();
          form.append('file', file);
          form.append('changeNote', changeNote.trim());
          await sopApi.addFileVersion(sopId, form);
        } else if (source === 'native') {
          await sopApi.addNativeVersion(sopId, { bodyMarkdown, changeNote: changeNote.trim() });
        }
      }

      navigate(`/tiles/ksp_sops/view/${sopId}`);
    } catch (err) {
      setError(err.message || 'Failed to save SOP');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const inputClass = `w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm
    text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`;

  return (
    <form onSubmit={submit} className="p-6 max-w-4xl mx-auto space-y-6">
      <Link
        to={isEdit ? `/tiles/ksp_sops/view/${sopId}` : '/tiles/ksp_sops/library'}
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="w-4 h-4" /> {isEdit ? 'Back to SOP' : 'Back to library'}
      </Link>

      <h2 className="text-2xl font-bold text-slate-100">{isEdit ? 'Edit SOP' : 'New SOP'}</h2>

      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">{error}</div>
      )}

      {/* --- source --- */}
      {!isEdit && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'file', icon: UploadCloud, label: 'Upload a file', hint: 'Word, PowerPoint, PDF, video — anything' },
            { key: 'native', icon: PenLine, label: 'Write it here', hint: 'Compose the SOP in the HQ editor' },
          ].map(({ key, icon: Icon, label, hint }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSource(key)}
              className={`flex flex-col items-start gap-1 p-4 rounded-lg border text-left transition ${
                source === key
                  ? 'bg-indigo-500/10 border-indigo-500 text-slate-100'
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'}`}
            >
              <Icon className={`w-5 h-5 ${source === key ? 'text-indigo-400' : 'text-slate-500'}`} />
              <span className="font-medium text-sm">{label}</span>
              <span className="text-xs text-slate-500">{hint}</span>
            </button>
          ))}
        </div>
      )}

      {/* --- metadata --- */}
      <div className="space-y-4">
        <div>
          <label htmlFor="sop-title" className="block text-sm font-medium text-slate-300 mb-1.5">
            Title <span className="text-rose-400">*</span>
          </label>
          <input
            id="sop-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Inbound Trailer Check-In"
            className={inputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="sop-description" className="block text-sm font-medium text-slate-300 mb-1.5">
            Description
          </label>
          <textarea
            id="sop-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What is this for, and when would someone need it? This text is searched."
            className={inputClass}
          />
          <p className="text-xs text-slate-500 mt-1">
            Write it the way someone would ask for it — the description is weighted heavily in search.
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-slate-300 mb-1.5">Tags</span>
          <TagPicker allTags={allTags} value={tags} onChange={setTags} />
        </div>
      </div>

      {/* --- content --- */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="block text-sm font-medium text-slate-300">
            {source === 'file' ? 'File' : 'Content'}
            {!isEdit && <span className="text-rose-400"> *</span>}
          </span>
          {source === 'native' && (
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200"
            >
              {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showPreview ? 'Hide preview' : 'Preview'}
            </button>
          )}
        </div>

        {source === 'file' ? (
          <>
            {isEdit && existing?.originalName && !file && (
              <div className="flex items-center gap-3 p-3 bg-slate-800/60 border border-slate-700 rounded-lg text-sm">
                <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-slate-200 truncate">{existing.originalName}</p>
                  <p className="text-xs text-slate-500">
                    Current file (v{existing.versionNo})
                    {existing.sizeBytes ? ` · ${formatBytes(existing.sizeBytes)}` : ''}
                  </p>
                </div>
              </div>
            )}
            <FileDropzone file={file} onFile={setFile} />
            {isEdit && (
              <p className="text-xs text-slate-500">
                Uploading a file adds a new version. The current one is kept in history.
              </p>
            )}
          </>
        ) : (
          <div className={showPreview ? 'grid gap-3 lg:grid-cols-2' : ''}>
            <textarea
              value={bodyMarkdown}
              onChange={(e) => setBodyMarkdown(e.target.value)}
              rows={20}
              placeholder={MARKDOWN_HINT}
              className={`${inputClass} font-mono text-sm resize-y`}
            />
            {showPreview && (
              <div className="overflow-auto max-h-[32rem]">
                <MarkdownBody source={bodyMarkdown || '*Nothing to preview yet.*'} />
              </div>
            )}
          </div>
        )}

        {source === 'native' && (
          <p className="text-xs text-slate-500">
            Supports Markdown: <code className="text-indigo-300"># heading</code>,{' '}
            <code className="text-indigo-300">**bold**</code>, lists, tables, and code blocks.
          </p>
        )}
      </div>

      {willAddVersion && (
        <div>
          <label htmlFor="sop-change-note" className="block text-sm font-medium text-slate-300 mb-1.5">
            What changed?
          </label>
          <input
            id="sop-change-note"
            type="text"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="e.g. Updated seal verification step for 2026 policy"
            className={inputClass}
          />
          <p className="text-xs text-slate-500 mt-1">Shown in the version history.</p>
        </div>
      )}

      {/* --- actions --- */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-700">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={status === 'draft'}
            onChange={(e) => setStatus(e.target.checked ? 'draft' : 'published')}
            className="rounded border-slate-600 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
          />
          Save as draft (hidden from the library)
        </label>

        <div className="flex items-center gap-2">
          <Link
            to={isEdit ? `/tiles/ksp_sops/view/${sopId}` : '/tiles/ksp_sops/library'}
            className="px-4 py-2 text-sm text-slate-300 hover:text-slate-100"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700
              text-white rounded-md text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create SOP'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SopEditor;
